import React, { useEffect, useMemo, useReducer, useState } from "react";
import BsModal from "../Common/BsModal";
import TextInput from "../Common/TextInput";
import Notification from "../Common/Notification";
import { useBanking } from "../../DataStore/BankingContext";
import ajaxBanking from "../../util/remote/ajaxBanking";

/**
 * NewAccountModal
 * - Modal UI to create a new ledger account (Chart of Accounts)
 * - Opening balance removed
 * - Bank linking removed: replaced with a clear guideline panel
 * - Emphasizes: only Asset accounts under parent "Bank" (or "Cash & Bank") are linkable to banks
 */
const reducer = (state, action) => {
  if (action.type === "RESET") return { ...initialState };
  return { ...state, [action.type]: action.value };
};

const initialState = {
  loading: false,

  bankName:'',
  accountNumber:'',
  phoneNumber:'',
  email:'',
  address: '',
  bankCode: ''
};

const UpdateBankModal = ({
  show,
  onClose,
  buttonLabel = "Create Bank",
  bank
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [currentNotify, setNotify] = useState(null);

  const { getBanks} = useBanking();

  useEffect(() => {
    if (show) {
      getBanks();
    } else {
      setNotify(null);
    }
  }, [show]);


  useEffect(() => {
    if (!currentNotify) return;
    const t = setTimeout(() => setNotify(null), 5000);
    return () => clearTimeout(t);
  }, [currentNotify]);

  useEffect(()=>{
    if(bank){
      dispatch({type: 'bankName', value: bank.bank_name})
      dispatch({type: 'accountNumber', value: bank.account_number})
      dispatch({type: 'email', value: bank.email || ''})
      dispatch({type: 'phoneNumber', value: bank.phone || ''})
      dispatch({type: 'address', value: bank.address || ''})
      dispatch({type: 'bankCode', value: bank.bank_code || ''})

    }
  }, [bank])


  const requireField = (condition, message) => {
    if (condition) {
      setNotify(<Notification type="danger" message={message} />);
      return true;
    }
    return false;
  };

  const handleSave = async () => {
    if (state.loading) return;
    if (requireField(!String(state.bankName || "").trim(), "Enter Bank Name.")) return;
    if (requireField(!String(state.accountNumber || "").trim(), "Enter an account number.")) return;


    dispatch({ type: "loading", value: true });

    const payload = {
    
      bank_name: state.bankName,
      account_number: state.accountNumber,
      phone_number: state.phoneNumber,
      email: state.email,
      address: state.address,
      bank_id: bank?.bank_id,
      bank_code: state.bankCode
    
    };


    dispatch({ type: 'loading', value: true });
   const server_response = bank ? await ajaxBanking.updateBank(payload) : await ajaxBanking.addBank(payload);
    dispatch({ type: 'loading', value: false });

    if (server_response.status === 'OK') {
        setNotify(<Notification type="success" message={server_response.message} />);
        if(!bank){
            dispatch({ type: 'RESET' });
        }
       
            /**
         * Refresh data in context after the success notification has showed up to mitigate the re-render effect.
         */
            setTimeout(() => {
            getBanks(true);
        }, 2000);
    } else {
        setNotify(<Notification type="danger" message={server_response.message} />);
    }


  };

  const draftTitle = useMemo(() => {
    const n = String(state.accountNumber || "").trim();
    const t = String(state.bankName || "").trim();
    if (!n && !t) return "Draft Bank";
    if (n && !t) return `Draft: ${n}`;
    if (!n && t) return `Draft: ${t}`;
    return `Draft: ${t}`;
  }, [state.accountNumber, state.bankName]);

  return (
    <BsModal
      show={show}
      onClose={onClose}
      title={bank ? "Edit Bank Details":"New Bank"}
      size="modal-lg"
      footer={
        <>
          <button type="button" className="btn btn-modern-secondary" onClick={onClose} disabled={state.loading}>
            Close
          </button>

          <button type="button" className="btn btn-modern-primary" onClick={handleSave} disabled={state.loading}>
            {state.loading ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" />
                Saving...
              </>
            ) : (
              <>
                <i className="fa fa-check-circle mr-1" /> {bank? "Save Changes": buttonLabel}
              </>
            )}
          </button>
        </>
      }
    >
      {/* Header strip (same vibe as your ManageBankModal) */}
      <div
        className="p-3 mb-3"
        style={{
          borderRadius: 12,
          border: "1px solid rgba(255,255,255,0.08)",
          background: "rgba(255,255,255,0.03)",
        }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <div
              className="mr-3 d-flex align-items-center justify-content-center"
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: "rgba(40,167,69,0.12)",
                border: "1px solid rgba(40,167,69,0.25)",
              }}
            >
              <i className="fa fa-book text-success" />
            </div>

            <div>
              <div className="font-weight-bold" style={{ fontSize: 16 }}>
                {draftTitle}
              </div>
              <div className="text-muted" style={{ fontSize: 13 }}>
                {state.accountNumber || "Bank Account Number"}
                {state.address ? ` • Phone: ${state.phoneNumber}` : ""}
              </div>
            </div>
          </div>

         
        </div>
      </div>

     
      {currentNotify}

      {/* Core inputs */}

       <div className="row">
        <div className="col-md-6">
          <TextInput
            type="text"
            label="Bank Name"
            placeholder="Bank Name"
            leftIcon="fa fa-bank"
            value={state.bankName}
            onChange={e => dispatch({type: 'bankName', value: e.target.value})}
            required
            wrap
            />
        </div>
        <div className="col-md-6">
            <TextInput
              type="text"
              label="Bank Code (Optional)"
              placeholder="Bank Code"
              leftIcon="fa fa-hashtag"
              value={state.bankCode}
              onChange={e => dispatch({type: 'bankCode', value: e.target.value})}
              required
              wrap
              />
          </div>

       
      </div>

      <div className="row">
        <div className="col-md-5">
           <TextInput
            type="text"
            label="Account Number"
            placeholder="Bank Account Number"
            leftIcon="fa fa-credit-card"
            value={state.accountNumber}
            onChange={e => dispatch({type: 'accountNumber', value: e.target.value})}
            wrap
            required
            />
          
        </div>

        <div className="col-md-7">
          <TextInput
                type="email"
                label="Email Address"
                placeholder="Email Address"
                leftIcon="fa fa-envelope"
                value={state.email}
                onChange={e => dispatch({type: 'email', value: e.target.value})}
                wrap
                required
                />
        </div>
      </div>
       <div className="row">
        <div className="col-md-5">
           <TextInput
              type="text"
              label="Bank Branch/Address"
              placeholder="Bank Address or Account Branch"
              leftIcon="fa fa-map-pin"
              value={state.address}
              onChange={e => dispatch({type: 'address', value: e.target.value})}
              wrap
              required
              />
        </div>

        <div className="col-md-7">
          <TextInput
              type="text"
              label="Phone Number"
              placeholder="Phone Number"
              leftIcon="fa fa-phone"
              value={state.phoneNumber}
              onChange={e => dispatch({type: 'phoneNumber', value: e.target.value})}
              wrap
              required
              />
        </div>
      </div>

     
      

      {/* helper: show guideline if user is in Assets or chose parent Bank, even if they didn't click "tips" */}
    </BsModal>
  );
};


export default UpdateBankModal;
